/**
 * The chart's box: margins, the plot area, and how many marks fit in it.
 *
 * One object the component reads, so the geometry is decided in a pure
 * function a test can call rather than in markup. The numbers are set by the
 * width this panel actually has: `minmax(320px, 33vw)` with 22 px of padding
 * is **276 to 430 px** on a laptop, and the mobile sheet at its `full` snap is
 * about **350 px**. Forty-eight hours across 300 px is 6.25 px an hour, which
 * is enough for a line and nowhere near enough for a label an hour.
 */

export interface Margins {
  top: number
  right: number
  bottom: number
  left: number
}

export interface Frame {
  width: number
  height: number
  margins: Margins
  /** The plot area, in SVG user units. */
  plot: { x: number; y: number; width: number; height: number }
  xRange: readonly [number, number]
  yRange: readonly [number, number]
  /** How many x marks to ask `timeTicks` for at this width. */
  xTicks: number
  /** How many y marks to ask `niceTicks` for at this height. */
  yTicks: number
}

/**
 * Room for a y label on the left and nothing on the right.
 *
 * The right margin is 8 rather than 0 so the last point's marker is not
 * clipped in half: a marker is 8 px across and a clipped one reads as a
 * rendering bug rather than as the end of a forecast.
 */
export const DEFAULT_MARGINS: Margins = {
  top: 10,
  right: 8,
  bottom: 20,
  left: 34,
}

/**
 * Roughly one x mark per this many pixels.
 *
 * Measured against the labels this axis actually draws — `5 PM`, `Sun 21` —
 * at 11 px: about 34 px of text plus air. Below this they collide, which at
 * 276 px is three or four marks rather than ten.
 */
const PX_PER_X_TICK = 56
const PX_PER_Y_TICK = 34

export function frame(width: number, height: number, margins: Partial<Margins> = {}): Frame {
  const m = { ...DEFAULT_MARGINS, ...margins }
  const plotWidth = Math.max(1, width - m.left - m.right)
  const plotHeight = Math.max(1, height - m.top - m.bottom)
  return {
    width,
    height,
    margins: m,
    plot: { x: m.left, y: m.top, width: plotWidth, height: plotHeight },
    xRange: [m.left, m.left + plotWidth],
    // Inverted, because SVG's y grows downwards and a temperature does not.
    yRange: [m.top + plotHeight, m.top],
    xTicks: Math.max(2, Math.min(12, Math.round(plotWidth / PX_PER_X_TICK))),
    yTicks: Math.max(2, Math.min(8, Math.round(plotHeight / PX_PER_Y_TICK))),
  }
}
